import { DestinationAdapter } from './base-adapter.js';

const META_PIXEL_SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';
const SCRIPT_LOAD_TIMEOUT_MS = 10000;

class MetaPixelAdapter extends DestinationAdapter {
  constructor() {
    super();
    this._vendorLoaded = false;
    this._loadPromise = null;
  }

  get name() {
    return 'META_PIXEL';
  }

  isInitialized() {
    return typeof window.fbq === 'function' &&
      (this._vendorLoaded || typeof window.fbq.callMethod === 'function');
  }

  _installBootstrapStub() {
    if (typeof window.fbq === 'function') {
      return;
    }

    const fbqStub = function() {
      if (typeof fbqStub.callMethod === 'function') {
        fbqStub.callMethod.apply(fbqStub, arguments);
      } else {
        fbqStub.queue.push(arguments);
      }
    };

    fbqStub.queue = [];
    fbqStub.push = fbqStub;
    fbqStub.loaded = false;
    fbqStub.version = '2.0';
    fbqStub.__eventfoundryStub = true;

    window.fbq = fbqStub;
    if (!window._fbq) {
      window._fbq = fbqStub;
    }
  }

  async _loadVendorScript() {
    if (this._loadPromise) {
      return this._loadPromise;
    }

    this._loadPromise = new Promise((resolve) => {
      let settled = false;
      const script = document.createElement('script');
      script.async = true;
      script.src = META_PIXEL_SCRIPT_URL;

      script.onload = () => {
        if (settled) return;
        settled = true;
        this._vendorLoaded = true;
        resolve(true);
      };

      script.onerror = () => {
        if (settled) return;
        settled = true;
        console.error('EventFoundry: Failed to load Meta Pixel script');
        resolve(false);
      };

      setTimeout(() => {
        if (settled) return;
        settled = true;
        console.error('EventFoundry: Meta Pixel load timeout');
        resolve(false);
      }, SCRIPT_LOAD_TIMEOUT_MS);

      document.head.appendChild(script);
    });

    const loaded = await this._loadPromise;
    this._loadPromise = null;
    return loaded;
  }

  async initialize(credentials) {
    const { pixelId } = credentials;

    if (!pixelId) {
      console.error('EventFoundry: Meta Pixel adapter missing pixelId');
      return false;
    }

    // Validate pixel ID format (numeric string)
    if (!pixelId.match(/^\d+$/)) {
      console.error(`EventFoundry: Invalid Meta Pixel ID format: ${pixelId}`);
      return false;
    }

    if (this.isInitialized()) {
      this._vendorLoaded = true;
      console.log('EventFoundry: Using existing Meta Pixel installation');
      return true;
    }

    this._installBootstrapStub();

    const vendorLoaded = await this._loadVendorScript();
    if (!vendorLoaded || !this.isInitialized()) {
      this._vendorLoaded = false;
      console.error('EventFoundry: Meta Pixel vendor script not available');
      return false;
    }

    // Initialize pixel
    window.fbq('init', pixelId);

    // Track automatic PageView
    window.fbq('track', 'PageView');

    console.log('EventFoundry: Meta Pixel initialized successfully');
    return true;
  }

  fireEvent(eventKey, eventDef, params) {
    if (!this.isInitialized()) {
      console.error('EventFoundry: Meta Pixel not initialized - cannot fire event');
      return;
    }

    // BETA: All events fire as Custom Events.
    // Spread base_params; adapters own any destination-specific enrichment.
    window.fbq('trackCustom', eventKey, { ...params });
  }
}

export const metaPixelAdapter = new MetaPixelAdapter();
